import { useDispatch, useSelector } from 'react-redux';

import { AppState, SettingsRequestActions, settingsSelectors } from '@fullstory/redux';
import { useEffect } from 'react';

const { getOrgHistory, getOrgAnalyticsHistory } = settingsSelectors;

const useFetchSettings = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(SettingsRequestActions.getSettings());
  }, [dispatch]);
  const orgHistory = useSelector(getOrgHistory);
  const orgAnalyticsHistory = useSelector(getOrgAnalyticsHistory);
  return { orgHistory, orgAnalyticsHistory };
};

/**
 * Checks whether a given date first into either the replay or analytics retention window.
 *
 * No date will default to true.
 *
 * @param history Either the org history (getOrgHistory selector)
 * or org analytics history (getOrgAnalyticsHistory selector).
 * @param date The date to check.
 * @param retentionTimestamp Either the retentionTimestamp or playbackRetentionTimestamp.
 */
const isInsideRetentionWindow = (
  history: number,
  date: Date | null | undefined,
  retentionTimestamp: number,
) => !history || !date || date >= new Date(retentionTimestamp);

/**
 * Takes a date and checks to see if it is within the analytics retention window or not. If
 * the range falls outside of "retentionTimestamp", then the resulting boolean
 * "insideRetentionWindow" will be false otherwise it is true.
 *
 * @param date date to check. If a date is not provided, we fallback to saying its within the retention window.
 * @returns insideRetentionWindow is true if the value is within the retention window, false if not
 * (resulting data loss)
 */
export const useOrgRetention = (date: Date | null | undefined) => {
  const { orgAnalyticsHistory } = useFetchSettings();
  const retentionTimestamp = useSelector((state: AppState) => state.fullstory.retentionTimestamp);
  return {
    insideRetentionWindow: isInsideRetentionWindow(orgAnalyticsHistory, date, retentionTimestamp),
    retentionWindowInMonths: orgAnalyticsHistory,
    retentionTimestamp,
  };
};

/**
 * Takes a date and checks to see if it is within the playback retention window or not.
 * @param date the latest date relevant in the session. If a date is not provided, we fallback to saying its within the retention window.
 * @returns insideRetentionWindow is true if the value is within the retention window, false if not
 */
export const usePlaybackRetention = (date: Date | null | undefined) => {
  const { orgHistory } = useFetchSettings();
  const retentionTimestamp = useSelector(
    (state: AppState) => state.fullstory.playbackRetentionTimestamp,
  );
  return {
    insideRetentionWindow: isInsideRetentionWindow(orgHistory, date, retentionTimestamp),
    retentionWindowInMonths: orgHistory,
    retentionTimestamp,
  };
};
